import { createFileRoute } from "@tanstack/react-router";
import { SEOPage } from "@/components/site/SEOPage";
import { CheckCircle2 } from "lucide-react";

export const Route = createFileRoute("/aplicativos-iptv/blessed-player")({
  head: () => ({
    meta: [
      { title: "Como Baixar e Instalar o Blessed Player na Samsung, LG e Roku TV | P2 PRO" },
      { name: "description", content: "Aprenda como baixar e instalar o aplicativo Blessed Player na sua Smart TV Samsung, LG ou Roku TV. Guia completo passo a passo atualizado em 2026." },
    ],
  }),
  component: () => (
    <SEOPage
      category="Tutorial"
      title="Como Baixar e Instalar o Blessed Player na Samsung, LG e Roku TV"
      subtitle="Blessed Player: Aplicativo Compatível com Painel IPTV Pós Pago"
      introParagraphs={[
        "O Blessed Player é um aplicativo de última geração, focado em alta performance e facilidade de uso em Smart TVs. Com suporte para Samsung, LG e Roku TV, ele oferece uma navegação fluida e rápida para seus conteúdos favoritos.",
        "Neste tutorial, mostraremos exatamente como instalar o Blessed Player em seu televisor de forma simples."
      ]}
      sections={[
        {
          title: "O Que é o Blessed Player?",
          type: "default",
          content: (
            <div className="space-y-6 text-muted-foreground leading-relaxed">
              <p>
                O Blessed Player é um reprodutor de mídia avançado disponível nas lojas oficiais das principais Smart TVs. Ele se destaca pela estabilidade e pela interface limpa, permitindo que o usuário foque no que realmente importa: o conteúdo.
              </p>
              <p>
                Sendo totalmente compatível com o sistema de Painel IPTV Pós Pago, o Blessed Player é uma das escolhas preferidas para quem busca uma solução robusta e profissional.
              </p>
            </div>
          )
        },
        {
          title: "Como Instalar o Blessed Player na Samsung Smart TV",
          type: "default",
          content: (
            <div className="space-y-6">
              <p className="text-lg text-muted-foreground italic">Siga o passo a passo abaixo:</p>
              <div className="grid gap-4">
                {[
                  { step: 1, text: "Abra o Smart Hub e vá até a loja Samsung Apps." },
                  { step: 2, text: "Clique na lupa de pesquisa." },
                  { step: 3, text: "Digite: Blessed Player" },
                  { step: 4, text: "Selecione o app e clique em Instalar." },
                  { step: 5, text: "Abra o aplicativo após o término." },
                  { step: 6, text: "Insira suas credenciais de acesso." },
                ].map((item) => (
                  <div key={item.step} className="flex items-start gap-4 p-5 rounded-2xl bg-white/[0.03] border border-white/5 hover:border-electric/30 transition-colors">
                    <span className="flex-shrink-0 w-10 h-10 rounded-full bg-electric/20 text-electric flex items-center justify-center font-black">
                      {item.step}
                    </span>
                    <p className="pt-2 text-muted-foreground font-medium">{item.text}</p>
                  </div>
                ))}
              </div>
              <p className="text-electric font-black uppercase tracking-widest text-sm">Blessed Player configurado com sucesso na Samsung!</p>
            </div>
          )
        },
        {
          title: "Como Instalar o Blessed Player na LG Smart TV",
          type: "default",
          content: (
            <div className="space-y-6">
              <p className="text-lg text-muted-foreground italic">Siga o passo a passo abaixo:</p>
              <div className="grid gap-4">
                {[
                  { step: 1, text: "Acesse a LG Content Store em sua TV." },
                  { step: 2, text: "Procure pelo ícone de pesquisa (Lupa)." },
                  { step: 3, text: "Pesquise por: Blessed Player" },
                  { step: 4, text: "Clique em Instalar." },
                  { step: 5, text: "Após instalar, abra o app no menu principal." },
                  { step: 6, text: "Faça login com sua conta IPTV." },
                ].map((item) => (
                  <div key={item.step} className="flex items-start gap-4 p-5 rounded-2xl bg-white/[0.03] border border-white/5 hover:border-electric/30 transition-colors">
                    <span className="flex-shrink-0 w-10 h-10 rounded-full bg-electric/20 text-electric flex items-center justify-center font-black">
                      {item.step}
                    </span>
                    <p className="pt-2 text-muted-foreground font-medium">{item.text}</p>
                  </div>
                ))}
              </div>
              <p className="text-electric font-black uppercase tracking-widest text-sm">Tutorial concluído para LG Smart TV.</p>
            </div>
          )
        },
        {
          title: "Como Instalar o Blessed Player na Roku TV",
          type: "default",
          content: (
            <div className="space-y-6">
              <p className="text-lg text-muted-foreground italic">Siga o passo a passo abaixo:</p>
              <div className="grid gap-4">
                {[
                  { step: 1, text: "No menu lateral, selecione Pesquisar." },
                  { step: 2, text: "Digite Blessed Player." },
                  { step: 3, text: "Selecione o canal oficial." },
                  { step: 4, text: "Clique em Adicionar Canal." },
                  { step: 5, text: "Aguarde a instalação e abra o canal." },
                  { step: 6, text: "Configure sua lista IPTV." },
                ].map((item) => (
                  <div key={item.step} className="flex items-start gap-4 p-5 rounded-2xl bg-white/[0.03] border border-white/5 hover:border-electric/30 transition-colors">
                    <span className="flex-shrink-0 w-10 h-10 rounded-full bg-electric/20 text-electric flex items-center justify-center font-black">
                      {item.step}
                    </span>
                    <p className="pt-2 text-muted-foreground font-medium">{item.text}</p>
                  </div>
                ))}
              </div>
              <p className="text-electric font-black uppercase tracking-widest text-sm">Sua Roku TV agora conta com o Blessed Player.</p>
            </div>
          )
        },
        {
          title: "Dispositivos Compatíveis",
          type: "featured",
          content: (
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                "Samsung Smart TV",
                "LG Smart TV",
                "Roku TV",
                "Outras TVs com lojas compatíveis"
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-3 bg-white/5 p-4 rounded-xl border border-white/5">
                  <CheckCircle2 className="w-5 h-5 text-electric shrink-0" />
                  <span className="text-muted-foreground font-bold uppercase text-[10px] tracking-widest">{item}</span>
                </div>
              ))}
            </div>
          )
        },
        {
          title: "Por que escolher o Blessed Player?",
          type: "default",
          content: (
            <div className="grid sm:grid-cols-2 gap-4 text-muted-foreground">
              {[
                "Rapidez no carregamento",
                "Interface limpa e intuitiva",
                "Suporte a múltiplos formatos",
                "Estabilidade garantida",
                "Configuração simplificada",
                "Totalmente integrado ao Pós Pago"
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-electric shrink-0" />
                  <span className="font-medium">{item}</span>
                </div>
              ))}
            </div>
          )
        }
      ]}
      benefits={[
        { title: "Alta Estabilidade", description: "Menos travamentos e carregamento ultra rápido." },
        { title: "Fácil Navegação", description: "Interface desenhada para controle remoto." },
        { title: "Design Moderno", description: "Experiência visual premium em sua Smart TV." }
      ]}
      howItWorks={[
        { title: "Busque na Loja", description: "Encontre o Blessed Player na loja de apps da sua TV." },
        { title: "Instale", description: "O processo é automático e leva menos de 1 minuto." },
        { title: "Conecte", description: "Use os dados do seu Painel IPTV Pós Pago." }
      ]}
      diferenciais={[
        { title: "Compatibilidade", description: "Funciona perfeitamente em Samsung, LG e Roku." },
        { title: "Integração", description: "Totalmente compatível com nossa estrutura de revenda." },
        { title: "Suporte", description: "Conte com auxílio especializado para configuração." }
      ]}
      faq={[
        { question: "O Blessed Player é gratuito?", answer: "O download na loja costuma ser gratuito, mas requer uma lista ativa do Painel IPTV Pós Pago para funcionar." },
        { question: "Funciona em TVs antigas?", answer: "Funciona em modelos Smart TV que possuem acesso às lojas de aplicativos oficiais." },
        { question: "É fácil de configurar?", answer: "Sim, basta inserir os dados de acesso fornecidos pelo seu revendedor após a instalação." },
        { question: "Tem suporte para Roku?", answer: "Sim, o Blessed Player está disponível na loja de canais da Roku TV." }
      ]}
      breadcrumbs={[
        { label: "Aplicativos", href: "/aplicativos-iptv" },
        { label: "Blessed Player", href: "/aplicativos-iptv/blessed-player" }
      ]}
      ctaTitle="🚀 Eleve sua Revenda com o Blessed Player"
      ctaDescription="Ofereça aos seus clientes os melhores aplicativos do mercado. Tenha seu próprio Painel IPTV Pós Pago com suporte ao Blessed Player, One X, Fun Play e muito mais."
      ctaButtonText="QUERO TESTAR O PAINEL IPTV PÓS PAGO AGORA"
    />
  ),
});